import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import I18n from 'I18n';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { shouldUseAnimationsGSWH } from '../experiments/gswhV1/gswhUtils';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { useButtonGridFocus } from '../hooks/useButtonGridFocus';
import { useButtonGridPreferredColumnCount } from '../hooks/useButtonGridPreferredColumnCount';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { getFlowKey } from '../routing/getFlowKey';
import { useSignupData } from '../signup-data/useSignupData';
import { ButtonsGridButton, ButtonsGridButtonHolder, ButtonsGridStyled } from '../styles/ButtonsGrid.styles';
import { EventName } from '../tracking/EventName';
import { getTracker } from '../tracking/getTracker';
import { useView } from '../views/useView';
import NextButtonWrapper from './NextButtonWrapper';

const defaultGetButtonText = ({
  dataKey,
  key
}) => {
  return I18n.text(`views.${dataKey}.options.${key}`);
};

export const ButtonsGrid = ({
  columnCount,
  optionHeight,
  options,
  dataKey,
  stepKey,
  type,
  hasNextButton = false,
  nextButtonLabel,
  shouldDisableAutofocus = false,
  getButtonDescriptionText,
  getButtonText = defaultGetButtonText,
  onSelect = () => {},
  onHighlightChange = () => {},
  setDataFn,
  isSelected = () => false
}) => {
  const gridRef = useRef(null);
  const flowKey = getFlowKey();
  const {
    submit
  } = useView({
    stepKey
  });
  const {
    data,
    setData
  } = useSignupData(dataKey);
  const {
    preferredColumnCount
  } = useButtonGridPreferredColumnCount(options, dataKey, getButtonText);
  const {
    isAssignedToVariant: isACQ134Variant
  } = useExperiment(ExperimentKeys.ACQ0134, TrackWhen.never);
  const shouldUseTransition = useSelector(shouldUseAnimationsGSWH);
  const actualColumnCount = columnCount || preferredColumnCount;
  const {
    currentStep
  } = useCurrentStep();
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();
  const {
    shouldUseLayoutTemplate
  } = useLayoutTemplate();

  const onSelectedChange = value => {
    onSelect(value);

    if (shouldUseTransition) {
      const buttonContainer = document.getElementsByClassName('buttonsGrid')[0];
      buttonContainer.addEventListener('animationend', () => {
        submit();
      });
    } else {
      submit();
    }
  };

  useButtonGridFocus(gridRef, options.length, shouldDisableAutofocus);

  const onClickOption = key => {
    if (setDataFn) {
      setDataFn(key, setData);
    } else {
      setData(key);
    }

    if (!hasNextButton) {
      onSelectedChange(key);
    } else {
      onHighlightChange(key);
      getTracker().track(EventName.Interaction, {
        action: `highlight ${dataKey} ${key}`
      });
    }
  };

  return /*#__PURE__*/_jsxs(ButtonsGridStyled // @ts-expect-error Expression produces a union type that is too complex to represent.
  , {
    className: "buttonsGrid",
    currentStep: currentStep,
    isFramedCentredStep: isFramedCentredStep,
    ref: gridRef,
    shouldUseLayoutTemplate: shouldUseLayoutTemplate // @ts-expect-error --option-preferred-column-count is not part of CSSProperties.
    ,
    style: Object.assign({}, actualColumnCount > 0 ? {
      '--option-preferred-column-count': actualColumnCount
    } : null),
    children: [options.map((key, index) => /*#__PURE__*/_jsx(ButtonsGridButtonHolder, {
      minHeight: optionHeight,
      children: /*#__PURE__*/_jsx(ButtonsGridButton, {
        block: true,
        className: "ButtonsGrid_Button",
        currentStep: currentStep,
        "data-selenium": `${dataKey}-${index}`,
        "data-selenium-option": key,
        description: getButtonDescriptionText && getButtonDescriptionText({
          dataKey,
          key,
          index
        }),
        flowKey: flowKey,
        isACQ134Variant: isACQ134Variant(),
        isFramedCentredStep: isFramedCentredStep,
        name: 'selectable-button',
        selected: data === key || isSelected(key),
        shouldUseLayoutTemplate: shouldUseLayoutTemplate,
        tabIndex: index,
        title: /*#__PURE__*/_jsx("p", {
          className: "m-all-0",
          children: getButtonText({
            dataKey,
            key,
            index
          })
        }),
        truncatable: false,
        type: type,
        onClick: () => onClickOption(key)
      })
    }, key)), hasNextButton && /*#__PURE__*/_jsx(NextButtonWrapper, {
      label: nextButtonLabel,
      onClick: () => onSelectedChange(data)
    })]
  });
};